import { Platform } from 'react-native';
import { APP_CONSTANTS } from '../config/constants';
import globalState, { syncActions } from '../state/globalState';
import { SyncLogger } from '../utils/logger';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '../config/recoveryDiagnostic';
import { isRecoveryRuntimeBlocked } from './recovery/RecoveryRuntimeGate';
import { captureSentryExceptionWithScope } from './monitoring/sentry';

const schedulerLogger = SyncLogger.Main;
const pushLogger = SyncLogger.Push;

// Intervalos básicos
const MIN_INTERVAL_MS = 60_000; // 1 minuto
const MAX_BACKOFF_MS = 15 * 60_000; // 15 minutos
const AUTO_PUSH_DEBOUNCE_MS = 2_000; // 2 segundos para agrupar pendências em lote
const AUTO_PUSH_RETRY_MS = 4_000; // 4 segundos quando já existir sync em andamento ou sem pré-requisito

type SchedulerOverrideConfig = {
  baseIntervalMs?: number;
  autoPushDebounceMs?: number;
  autoPushRetryMs?: number;
};

let overrideConfig: SchedulerOverrideConfig | null = null;

type SyncServiceModule = typeof import('./syncService');
type SyncServiceLoader = () => Promise<SyncServiceModule>;

let customLoader: SyncServiceLoader | null = null;

const loadSyncService: SyncServiceLoader = () => {
  if (customLoader) {
    return customLoader();
  }

  return import('./syncService');
};

let periodicTimeout: ReturnType<typeof setTimeout> | null = null;
let periodicDeadline: number | null = null;
let schedulerStarted = false;
let consecutiveFailures = 0;
let lastRunSource: 'interval' | 'trigger' | null = null;
let beforeUnloadListener: (() => void) | null = null;

let autoPushTimeout: ReturnType<typeof setTimeout> | null = null;
let autoPushReason: string | null = null;

const getBaseInterval = (): number => {
  const configured = overrideConfig?.baseIntervalMs ?? APP_CONSTANTS.TIMEOUTS.SYNC_INTERVAL;
  return Math.max(configured, MIN_INTERVAL_MS);
};

const getAutoPushDebounce = (): number => overrideConfig?.autoPushDebounceMs ?? AUTO_PUSH_DEBOUNCE_MS;

const getAutoPushRetry = (): number => overrideConfig?.autoPushRetryMs ?? AUTO_PUSH_RETRY_MS;

const sanitizeScheduleReason = (reason: string): string => {
  const normalized = String(reason || 'pending-operation')
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '<id>')
    .replace(/[^a-z0-9:_-]+/gi, '_')
    .slice(0, 80);
  if (!normalized || normalized === 'pending-operation') return 'pending-operation';
  return /^(?:guincho_[a-z0-9_]+|manual|periodic|realtime|foreground|background)(?::[a-z0-9_]+)?$/i.test(normalized)
    ? normalized
    : 'other';
};

const reportSynchronousPushScheduleFailure = (reason: string, error: unknown): void => {
  const safeReason = sanitizeScheduleReason(reason);
  const sanitizedError = new Error('sync_scheduler_failed:schedule_push_after_pending');
  sanitizedError.name = 'SyncSchedulerError';
  pushLogger.warn(`[auto-push] Falha síncrona ao agendar push (reason=${safeReason})`);
  captureSentryExceptionWithScope(sanitizedError, {
    tags: {
      flow: 'sync_scheduler',
      stage: 'schedule_push_after_pending',
      error_code: 'schedule_failed',
    },
    context: {
      reason: safeReason,
      error_type: error instanceof TypeError ? 'TypeError' : error instanceof Error ? 'Error' : typeof error,
    },
    fingerprint: ['sync_scheduler', 'schedule_push_after_pending', 'schedule_failed'],
    dedupeKey: `sync_scheduler:schedule_push_after_pending:${safeReason}`,
    dedupeWindowMs: 60_000,
  });
};

const computeNextDelay = (): number => {
  if (consecutiveFailures <= 0) {
    return getBaseInterval();
  }

  const factor = 2 ** consecutiveFailures;
  const delay = getBaseInterval() * factor;
  return Math.min(delay, MAX_BACKOFF_MS);
};

const clearPeriodicTimeout = () => {
  if (periodicTimeout) {
    clearTimeout(periodicTimeout);
    periodicTimeout = null;
  }
  periodicDeadline = null;
};

const clearAutoPushTimeout = () => {
  if (autoPushTimeout) {
    clearTimeout(autoPushTimeout);
    autoPushTimeout = null;
  }
  autoPushReason = null;
};

const detachBeforeUnload = () => {
  if (Platform.OS === 'web' && beforeUnloadListener) {
    window.removeEventListener('beforeunload', beforeUnloadListener);
    beforeUnloadListener = null;
  }
};

const scheduleNextRun = (delay: number, source: 'interval' | 'trigger' = 'interval') => {
  if (!schedulerStarted) {
    schedulerLogger.debug('[scheduler] Scheduler não iniciado, ignorando agendamento');
    return;
  }

  // Preserva o menor prazo e a intenção de continuar, mesmo se o timer anterior era periódico.
  let deadline = Date.now() + delay;
  if (periodicTimeout && periodicDeadline !== null && periodicDeadline <= deadline) {
    if (source === 'interval') return;
    deadline = periodicDeadline;
    delay = Math.max(0, deadline - Date.now());
  }
  clearPeriodicTimeout();
  periodicDeadline = deadline;
  periodicTimeout = setTimeout(() => {
    periodicTimeout = null;
    periodicDeadline = null;
    void executeScheduledSync(source);
  }, delay);

  schedulerLogger.info(`[scheduler] Próxima verificação agendada para ${delay}ms (falhas consecutivas: ${consecutiveFailures})`);
};

const executeScheduledSync = async (source: 'interval' | 'trigger'): Promise<void> => {
  if (!schedulerStarted) {
    schedulerLogger.debug('[scheduler] Trigger recebido porém scheduler está parado');
    return;
  }

  lastRunSource = source;

  try {
    const { checkSyncPrerequisites, syncAll } = await loadSyncService();

    const prerequisites = checkSyncPrerequisites();
    if (!prerequisites.allMet) {
      schedulerLogger.info('[scheduler] Pré-requisitos não atendidos, adiando sincronização automática');
      consecutiveFailures = 0; // não considerar como falha efetiva
      scheduleNextRun(getBaseInterval());
      return;
    }

    if (globalState.sync.isRunning.get()) {
      schedulerLogger.info('[scheduler] Sincronização já em andamento, pulando execução automática');
      consecutiveFailures = 0;
      scheduleNextRun(source === 'trigger' ? AUTO_PUSH_RETRY_MS : getBaseInterval(), source);
      return;
    }

    schedulerLogger.info(`[scheduler] Executando syncAll automático (source=${source})`);
    const result = await syncAll({ tracePrefix: '[scheduler]' });

    if (result.success) {
      schedulerLogger.info('[scheduler] Sincronização automática concluída com sucesso');
      consecutiveFailures = 0;
      syncActions.resetAttempts();
    } else {
      const isBusy = result.details?.reason === 'busy';
      if (isBusy) {
        schedulerLogger.info('[scheduler] syncAll retornou busy — preservando continuação do gatilho');
        consecutiveFailures = 0;
        scheduleNextRun(source === 'trigger' ? getAutoPushRetry() : getBaseInterval(), source);
      } else {
        consecutiveFailures = Math.min(consecutiveFailures + 1, 5);
        schedulerLogger.warn(`[scheduler] Sincronização automática retornou falha: ${result.message}`);
      }
    }
  } catch (error) {
    consecutiveFailures = Math.min(consecutiveFailures + 1, 5);
    schedulerLogger.error('[scheduler] Erro inesperado ao executar syncAll automático:', error);
  } finally {
    scheduleNextRun(computeNextDelay());
  }
};

export const startPeriodicSync = () => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    schedulerLogger.warn('[RECOVERY] Scheduler bloqueado pela quarentena');
    return;
  }

  if (isRecoveryRuntimeBlocked()) {
    schedulerLogger.warn('[RECOVERY] Scheduler bloqueado pelo gate runtime');
    return;
  }

  if (schedulerStarted) {
    schedulerLogger.info('[scheduler] Scheduler já está ativo, ignorando start duplicado');
    return;
  }

  schedulerStarted = true;
  consecutiveFailures = 0;

  if (Platform.OS === 'web' && !beforeUnloadListener) {
    beforeUnloadListener = () => {
      schedulerLogger.debug('[scheduler] beforeunload → cancelando timers automáticos');
      stopPeriodicSync();
    };
    window.addEventListener('beforeunload', beforeUnloadListener);
  }

  schedulerLogger.info('[scheduler] Scheduler iniciado');
  scheduleNextRun(getBaseInterval());
};

export const stopPeriodicSync = () => {
  if (!schedulerStarted) {
    return;
  }

  schedulerLogger.info('[scheduler] Scheduler parado');
  schedulerStarted = false;
  consecutiveFailures = 0;
  lastRunSource = null;
  clearPeriodicTimeout();
  clearAutoPushTimeout();
  detachBeforeUnload();
};

export const triggerSyncSoon = (reason: string = 'manual', delayMs: number = 2_000) => {
  if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
    schedulerLogger.warn(`[RECOVERY] triggerSyncSoon(${reason}) bloqueado pela quarentena`);
    return;
  }

  if (isRecoveryRuntimeBlocked()) {
    schedulerLogger.warn(`[RECOVERY] triggerSyncSoon(${reason}) bloqueado pelo gate runtime`);
    return;
  }

  if (!schedulerStarted) {
    schedulerLogger.debug(`[scheduler] triggerSyncSoon(${reason}) ignorado — scheduler não iniciado`);
    return;
  }

  schedulerLogger.info(`[scheduler] triggerSyncSoon(${reason}) agendando sincronização em ${delayMs}ms`);
  scheduleNextRun(delayMs, 'trigger');
};

const scheduleAutoPushRetry = (reason: string) => {
  clearAutoPushTimeout();
  const retryDelay = getAutoPushRetry();
  autoPushTimeout = setTimeout(() => {
    void executeAutoPush(reason);
  }, retryDelay);
  schedulerLogger.info(`[auto-push] Reagendando push automático em ${retryDelay}ms (reason=${reason})`);
};

const executeAutoPush = async (reason: string): Promise<void> => {
  autoPushTimeout = null;

  if (globalState.database.pendingChanges.get() <= 0) {
    pushLogger.info(`[auto-push] Nenhuma pendência para enviar (reason=${reason}), cancelando`);
    return;
  }

  if (globalState.sync.isRunning.get()) {
    pushLogger.info('[auto-push] Sincronização em andamento, adiando push automático');
    scheduleAutoPushRetry(reason);
    return;
  }

  try {
    const { checkSyncPrerequisites, pushLocalChanges } = await loadSyncService();

    const prerequisites = checkSyncPrerequisites();
    if (!prerequisites.allMet) {
      pushLogger.warn('[auto-push] Pré-requisitos não atendidos, tentando novamente em breve');
      scheduleAutoPushRetry(reason);
      return;
    }

    pushLogger.info(`[auto-push] Executando pushLocalChanges automático (reason=${reason})`);
    const result = await pushLocalChanges({ skipPull: true, tracePrefix: '[auto-push]' });

    if (!result.success) {
      pushLogger.warn(`[auto-push] Push automático retornou parcial/falha: ${result.message}`);
    } else {
      pushLogger.info('[auto-push] Push automático concluído com sucesso');
    }
  } catch (error) {
    pushLogger.error('[auto-push] Erro inesperado durante push automático:', error);
    scheduleAutoPushRetry(reason);
  }
};

export const schedulePushAfterPending = (reason: string = 'pending-operation') => {
  try {
    if (IS_CHECKLIST_RECOVERY_DIAGNOSTIC) {
      pushLogger.warn(`[RECOVERY] Auto-push(${reason}) bloqueado pela quarentena`);
      return;
    }

    if (isRecoveryRuntimeBlocked()) {
      pushLogger.warn(`[RECOVERY] Auto-push(${reason}) nao agendado pelo gate runtime`);
      clearAutoPushTimeout();
      return;
    }

    autoPushReason = reason;

    if (globalState.database.pendingChanges.get() <= 0) {
      pushLogger.debug('[auto-push] Contador de pendências é zero, não agendando push');
      return;
    }

    if (autoPushTimeout) {
      clearTimeout(autoPushTimeout);
    }

    const debounceDelay = getAutoPushDebounce();

    autoPushTimeout = setTimeout(() => {
      void executeAutoPush(reason);
    }, debounceDelay);

    pushLogger.info(`[auto-push] Push automático agendado para ${debounceDelay}ms (reason=${reason})`);
  } catch (error) {
    reportSynchronousPushScheduleFailure(reason, error);
    throw error;
  }
};

export const __setSyncServiceLoaderForTests = (loader: SyncServiceLoader | null) => {
  customLoader = loader;
};

export const __resetSchedulerForTests = () => {
  stopPeriodicSync();
  clearAutoPushTimeout();
  customLoader = null;
  consecutiveFailures = 0;
  lastRunSource = null;
  overrideConfig = null;
};

export const __getLastRunSourceForTests = () => lastRunSource;

export const __setSchedulerTestConfig = (config: SchedulerOverrideConfig | null) => {
  overrideConfig = config;
};
